<template>
  <OrderConfirmationStatusNew :reservationId="reservationId" :reservationKey="reservationKey">
    <h1 class="text-3xl font-bold font-heading mb-8">
      <span v-if="guest" id="guest-name">{{ guest.first_name }}, </span>Your Order Is Confirmed!
    </h1>
    <div
      v-if="currentShow && performance && reservation"
      class="flex flex-col lg:flex-row lg:space-x-20"
    >
      <div class="w-full lg:w-2/5">
        <div class="flex space-x-4 mb-6">
          <div class="rounded-md w-20 h-20 border border-linear-strong overflow-hidden">
            <picture>
              <source
                v-if="currentShow.srcset"
                :srcset="buildShowSrcset(currentShow.srcset, 'square')"
                sizes="80px"
                type="image/webp"
              />
              <img
                :src="currentShow.image"
                :alt="currentShow.name"
                class="w-full h-full object-cover"
              />
            </picture>
          </div>
          <div>
            <h2 class="font-heading font-semibold text-lg" id="show-name">
              {{ currentShow.name }}
            </h2>
            <span class="font-sans" id="formattedDateTime">
              <span id="performanceDate" class="font-sans inline">{{ performanceDate }}</span>
              <span id="showtime" class="block">{{ showtime }}</span>
            </span>
          </div>
        </div>

        <div id="numberOfGuests">{{ numberOfGuests }} Guests</div>
        <div id="seatingTimeDisplay" class="mb-4">{{ seating_time?.time }} Seating Time</div>

        <OrderConfirmationDetailTable />

        <div class="font-sans my-8" v-if="guest">
          We received your order and sent an email confirmation to {{ guest.email }}.
        </div>
        <div>Thank you!</div>
        <div>{{ venue_name }}</div>
      </div>

      <div class="w-full lg:w-3/5 mt-12 flex justify-end">
        <RoomMap
          id="room-map"
          role="img"
          :ariaLabel="roomMapAriaLabel"
          :backgroundImageAriaLabel="backgroundImageAriaLabel"
          :backgroundMode="roomMapBgMode"
          :roomLayout="performance.room_layout"
        >
          <RoomTable
            v-for="table in performance.tables"
            :key="table.id"
            :height="table.h"
            :width="table.w"
            :rotation="table.rotation"
            :shape="table.shape"
            :x="table.x"
            :y="table.y"
            :marker="reservation.tables.includes(table.id) ? 'checkmark' : 'none'"
            :fillClass="
              reservation.tables.includes(table.id) ? 'fill-primary' : 'fill-table-available'
            "
          />
        </RoomMap>
      </div>
    </div>
    <div class="mt-8 text-button-guest-primary">
      <router-link :to="{ name: 'setShow' }"> Return to shows. </router-link>
    </div>
    <p class="mt-8 text-sm text-fg-disclaimer" v-if="reservation">
      Reservation ID: {{ reservation.id }}
    </p>
  </OrderConfirmationStatusNew>
</template>

<script lang="ts" setup>
import { useCssVar } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import RoomTable from '@/common/components/molecules/RoomTable.vue'
import RoomMap from '@/common/components/organisms/RoomMap.vue'
import { ROOM_MAP_BG_MODE, RoomMapBgMode } from '@/common/interfaces/RoomMap'
import { StoreShow, buildShowSrcset } from '@/models'
import OrderConfirmationDetailTable from '@/modules/guestUi/components/organisms/OrderConfirmationDetailTable.vue'
import OrderConfirmationStatusNew from '@/modules/guestUi/components/organisms/OrderConfirmationStatusNew.vue'
import { useRoomMapAriaLabels } from '@/modules/guestUi/composables/useRoomMapAriaLabels'
import useOrderConfirmationStore from '@/modules/guestUi/stores/OrderConfirmationStore'
import { useSettingStore } from '@/stores/setting'

import { GuestRead } from '@generated/types'

defineProps<{
  reservationId: string
  reservationKey: string
}>()

const mode = useCssVar('--mode')
const roomMapBgMode = computed<RoomMapBgMode>(() => {
  return mode.value || (ROOM_MAP_BG_MODE.FORCE_LIGHT as RoomMapBgMode)
})

const settingStore = useSettingStore()
const { venue_name } = storeToRefs(settingStore)

const orderConfirmationStore = useOrderConfirmationStore()
const { performance, reservation, seating_time } = storeToRefs(orderConfirmationStore)

const confirmedTableIds = computed(() => reservation.value?.tables || [])
const { roomMapAriaLabel, backgroundImageAriaLabel } = useRoomMapAriaLabels({
  performance,
  selectedTableIds: confirmedTableIds,
  context: 'confirmed',
})

const currentShow = computed<StoreShow | undefined>(() => performance.value?.show)
const guest = computed<GuestRead | undefined>(() => reservation.value?.contact)
const numberOfGuests = computed<number>(() => reservation.value?.number_of_guests ?? 0)
const showtime = computed<string>(() => {
  if (!performance.value) return ''
  return `${settingStore.timeStr(performance.value.datetime)} Showtime`
})
const performanceDate = computed<string>(() => {
  if (!performance.value) return ''
  return `${settingStore.fullDate(performance.value.date)}`
})
</script>
